<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DocumentsAPI from 'dashboard/api/documents';

const props = defineProps({
  documentId: { type: Number, required: true },
});

const emit = defineEmits(['saved']);
const { t } = useI18n();

const dialogRef = ref(null);
const iframeUrl = ref('');
const isLoading = ref(false);
const error = ref('');

const reset = () => {
  iframeUrl.value = '';
  error.value = '';
};

const extractErrorMessage = err =>
  err?.response?.data?.error?.[0] ||
  err?.response?.data?.reason ||
  err?.message ||
  t('DOCUMENTS.SIGNATURES.BUILDER.ERROR_LOAD');

const handleMessage = event => {
  if (event.data?.type === 'docuseal:templateSaved') {
    const templateId = event.data.templateId ?? event.data.template_id;
    emit('saved', templateId ?? null);
    close();
  }
};

onMounted(() => {
  window.addEventListener('message', handleMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleMessage);
});

const open = async () => {
  reset();
  dialogRef.value?.open();
  isLoading.value = true;
  try {
    const { data } = await DocumentsAPI.signatureBuilder(props.documentId);
    if (data.url) {
      iframeUrl.value = data.url;
    } else {
      error.value = data.reason || t('DOCUMENTS.SIGNATURES.BUILDER.ERROR_LOAD');
    }
  } catch (err) {
    error.value = extractErrorMessage(err);
  } finally {
    isLoading.value = false;
  }
};

const close = () => dialogRef.value?.close();

const handleClose = () => {
  reset();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('DOCUMENTS.SIGNATURES.BUILDER.TITLE')"
    :description="t('DOCUMENTS.SIGNATURES.BUILDER.DESCRIPTION')"
    width="5xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <div v-if="error" class="text-sm text-n-ruby-11">
      {{ error }}
    </div>
    <div v-else-if="isLoading" class="flex items-center justify-center min-h-[400px]">
      <span class="text-sm text-n-slate-11">{{ t('DOCUMENTS.SIGNATURES.BUILDER.LOADING') }}</span>
    </div>
    <div v-else-if="iframeUrl" class="min-h-[600px]">
      <iframe
        :src="iframeUrl"
        class="w-full h-[600px] border-0"
        allow="clipboard-write"
      />
    </div>
  </Dialog>
</template>
